import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { testimonialService } from '../../services/testimonialService.js';
import { MessageSquareQuote, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const TestimonialList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchTestimonials();
  }, [currentPage]);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialService.getAllTestimonials(currentPage, perPage, false, true);
      setTestimonials(response.data.data || []);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load testimonials');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testimonialId, testimonialName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the testimonial from "${testimonialName}"? This action cannot be undone.`,
      'Delete Testimonial'
    );

    if (confirmed) {
      try {
        await testimonialService.deleteTestimonial(testimonialId);
        Alert.success('Testimonial deleted successfully!');
        fetchTestimonials();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete testimonial');
      }
    }
  };

  const renderStars = (rating) => '⭐'.repeat(rating);

  const renderProfileImage = (testimonial) => {
    const imageSrc = testimonial.image_base64
      ? `data:image/jpeg;base64,${testimonial.image_base64}`
      : null;
    const hasError = imageErrors[testimonial.id];

    if (imageSrc && !hasError) {
      return (
        <img
          src={imageSrc}
          alt={testimonial.full_name}
          className="w-10 h-10 rounded-full object-cover border border-gray-200"
          onError={() => setImageErrors((prev) => ({ ...prev, [testimonial.id]: true }))}
        />
      );
    }

    return (
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-lg">
        👤
      </div>
    );
  };

  const columns = [
    {
      key: 'image',
      label: 'Image',
      width: '80px',
      render: (testimonial) => renderProfileImage(testimonial),
    },
    {
      key: 'full_name',
      label: 'Customer Name',
      render: (testimonial) => (
        <button
          onClick={() => navigate(`/testimonials/view/${testimonial.id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {testimonial.full_name}
        </button>
      ),
    },
    { key: 'location', label: 'Location' },
    {
      key: 'rating',
      label: 'Rating',
      render: (testimonial) => renderStars(testimonial.rating),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (testimonial) => (
        <StatusBadge status={testimonial.is_active ? 'active' : 'inactive'} />
      ),
    },
    {
      key: 'created_at',
      label: 'Created Date',
      render: (testimonial) =>
        new Date(testimonial.created_at).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
  ];

  const getActions = (testimonial) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/testimonials/view/${testimonial.id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/testimonials/edit/${testimonial.id}`),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(testimonial.id, testimonial.full_name),
    },
  ];

  return (
    <PageLayout
      title="Testimonials"
      icon={MessageSquareQuote}
      actions={
        <Button icon={Plus} onClick={() => navigate('/testimonials/add')}>
          Add Testimonial
        </Button>
      }
    >
      <DataTable
        columns={columns}
        data={testimonials}
        loading={loading}
        emptyMessage="No testimonials found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default TestimonialList;
